/**
 * The live packet tail.
 *
 * The accumulated buffer lives in the TanStack Query cache rather than
 * `useState`, and that is a constraint rather than a preference: Radix unmounts
 * an inactive TabsContent, so leaving the Incoming tab destroys this hook's
 * component outright. Component state would be lost; cache state survives for
 * `gcTime`, so coming back shows the previous packets and resumes from the
 * retained cursor.
 *
 * All the merge logic is in `lib/dataLayer/tail.ts` so it can be tested. This
 * file is only the plumbing.
 */
import { useCallback } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { dataLayerApi } from '@/api/dataLayerApi'
import { PAGE_LIMIT } from '@/lib/dataLayer/params'
import {
  applyPage,
  clearTail,
  emptyTail,
  nextRequest,
  resetIfFilterChanged,
  tailFilterKey,
  type TailState,
} from '@/lib/dataLayer/tail'
import type { Hl7MessageFilter } from '@/lib/dataLayer/types'
import { dataLayerOverviewKey, DATA_LAYER_POLL_MS, type DataLayerOverview } from './useDataLayerOverview'

export function useDataLayerTail(filter: Hl7MessageFilter, enabled: boolean) {
  const queryClient = useQueryClient()
  const filterKey = tailFilterKey(filter)
  // The filter is part of the key, so changing it starts a fresh cache entry at
  // cursor 0 — which is exactly the reset a filter change needs, for free.
  const queryKey = ['data-layer', 'tail', filterKey] as const

  const query = useQuery<TailState>({
    queryKey,
    queryFn: async () => {
      const cached = queryClient.getQueryData<TailState>(queryKey) ?? emptyTail(filterKey)
      const previous = resetIfFilterChanged(cached, filterKey)

      const page = await dataLayerApi.listMessages({
        ...filter,
        ...nextRequest(previous),
        limit: PAGE_LIMIT,
      })

      // Restart detection reads the uptime the overview query already polled,
      // rather than spending a third request on it.
      const overview = queryClient.getQueryData<DataLayerOverview>(dataLayerOverviewKey)
      return applyPage(previous, page, { uptimeSec: overview?.status.uptimeSec ?? null })
    },
    enabled,
    // Same halt-on-error rule as the overview poll: a source that died
    // mid-session must not be re-dialled every tick forever.
    refetchInterval: (query) => (query.state.status === 'error' ? false : DATA_LAYER_POLL_MS),
    staleTime: 0,
    retry: false,
    // Long enough that tabbing away and back keeps the log.
    gcTime: 5 * 60_000,
  })

  /** Empty the display after a server-side clear, keeping the cursor valid. */
  const clear = useCallback(() => {
    queryClient.setQueryData<TailState>(queryKey, (current) =>
      current ? clearTail(current) : current,
    )
  }, [queryClient, queryKey])

  /** Drop the cursor and refetch a whole page. Recovers a gap or a stale cursor. */
  const reload = useCallback(() => {
    queryClient.setQueryData<TailState>(queryKey, emptyTail(filterKey))
    queryClient.invalidateQueries({ queryKey })
  }, [queryClient, queryKey, filterKey])

  return {
    tail: query.data ?? emptyTail(filterKey),
    error: query.error,
    isLoading: query.isLoading,
    clear,
    reload,
  }
}
